/**
 * ENTITLEMENT (B1) — local-first read of the materialized PROJECTION.
 *
 * The client **only reads**: `entitlement_state` and `entitled_course` are
 * written by the server's materializer, and the sync write-path rejects any
 * op on them (otherwise a student could self-grant access).
 *
 * These rows exist so the app can be HONEST — "you can't watch the recording
 * because you have a pending payment, and this comes from your cohort's
 * policy" — instead of showing a bare error. The real barrier is on the
 * server: the REST gates and the write-path.
 */

import {
  and,
  entitled_course,
  entitlement_state,
  eq,
  type PowerSyncDrizzleDB,
} from "@space/powersync";

/** Entitlement cache keys (TanStack Query). */
export const entitlementQueryKeys = {
  /** Prefix: invalidating it cascades to states, policies, and preview. */
  all: ["entitlement"] as const,
  states: (orgId: string | undefined) => ["entitlement", "states", orgId] as const,
  courses: (userId: string | undefined) => ["entitlement", "courses", userId] as const,
  /** Admin REST (not PowerSync): the configured cascade. */
  policies: (orgId: string | undefined) => ["entitlement", "policies", orgId] as const,
  /** Admin REST: "who this would block" (simulation). */
  preview: (orgId: string | undefined) => ["entitlement", "preview", orgId] as const,
};

/** All my entitlement states in an academy (academy + per cohort). */
export function getEntitlementStatesQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db.select().from(entitlement_state).where(eq(entitlement_state.organization_id, orgId));
}

/** My entitlement row for a course (`full` | `frozen`), if I have one. */
export function getEntitledCourseQuery(db: PowerSyncDrizzleDB, courseId: string) {
  return db.select().from(entitled_course).where(eq(entitled_course.course_id, courseId)).limit(1);
}

/** My aggregate academy state (the one shown in the global banner). */
export function getOrgEntitlementStateQuery(db: PowerSyncDrizzleDB, orgId: string) {
  return db
    .select()
    .from(entitlement_state)
    .where(
      and(eq(entitlement_state.organization_id, orgId), eq(entitlement_state.scope_level, "org")),
    )
    .limit(1);
}
